import { formatCount } from '@/utils/stringFormatters'
import {
  Horizontal,
  Texto,
  Vertical,
  useThemeContext,
} from '@gravitate-js/excalibrr'
import { Card } from 'antd'
import type { KeyboardEvent, MouseEvent } from 'react'
import {
  type CategoryCount,
  ISSUE_BUCKET_KEYS,
  type IssueBucket,
  type IssueBucketKey,
  type IssueCounts,
  type IssueSelection,
} from './issueCounts'
import { issueToneColor } from './issueFields'

const BUCKET_TITLES: Record<IssueBucketKey, string> = {
  issues: 'Issues',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  warnings: 'Warnings',
}

/** Fires Enter and Space like a click, since these are divs playing button. */
function activateOnKey(activate: () => void) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      activate()
    }
  }
}

interface CategoryRowProps {
  category: CategoryCount
  selected: boolean
  onSelect: () => void
}

function CategoryRow({ category, selected, onSelect }: CategoryRowProps) {
  // A plain div rather than excalibrr's Horizontal because the click has to
  // be stopped here: the row sits inside a card that is itself a filter, so
  // otherwise picking a category would also read as "select the whole bucket".
  const select = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onSelect()
  }

  return (
    <div
      role='button'
      tabIndex={0}
      aria-pressed={selected}
      onClick={select}
      onKeyDown={activateOnKey(onSelect)}
      className='rounded-sm cursor-pointer hover:bg-[var(--bg-2)]'
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        padding: '2px 4px',
        margin: '0 -4px',
        background: selected ? 'var(--bg-2)' : undefined,
      }}
    >
      <Texto style={{ fontSize: 12 }} weight={selected ? 700 : undefined}>
        {category.label}
      </Texto>
      <Texto style={{ fontSize: 12 }} weight={selected ? 700 : undefined}>
        {formatCount(category.count)}
      </Texto>
    </div>
  )
}

interface IssueCountCardProps {
  bucket: IssueBucketKey
  counts: IssueBucket
  selection: IssueSelection
  onSelect: (selection: IssueSelection) => void
}

function IssueCountCard({
  bucket,
  counts,
  selection,
  onSelect,
}: IssueCountCardProps) {
  const { isDarkTheme } = useThemeContext()
  const selected = selection.bucket === bucket

  const selectBucket = () => onSelect({ bucket, category: null })
  const selectCategory = (category: string) =>
    // Clicking the row you are already filtered to takes the filter back off.
    onSelect({
      bucket,
      category: selected && selection.category === category ? null : category,
    })

  return (
    <Card
      size='small'
      hoverable
      role='button'
      tabIndex={0}
      aria-pressed={selected}
      onClick={selectBucket}
      onKeyDown={activateOnKey(selectBucket)}
      style={{
        flex: 1,
        minWidth: 0,
        // Kept on the card in both states so selecting one does not shift the
        // row by a pixel.
        border: `2px solid ${selected ? 'hsl(var(--primary))' : 'transparent'}`,
      }}
      styles={{ body: { padding: 12 } }}
    >
      <Vertical gap={10}>
        <Horizontal justifyContent='space-between' alignItems='center' gap={8}>
          <Texto
            weight={700}
            textTransform='uppercase'
            style={{
              fontSize: 11,
              letterSpacing: 0.5,
              color: issueToneColor(bucket, isDarkTheme),
            }}
          >
            {BUCKET_TITLES[bucket]}
          </Texto>
          <Texto weight='bold' style={{ fontSize: 24 }}>
            {formatCount(counts.total)}
          </Texto>
        </Horizontal>
        <Vertical gap={2}>
          {counts.categories.map((category) => (
            <CategoryRow
              key={category.category}
              category={category}
              selected={selected && selection.category === category.category}
              onSelect={() => selectCategory(category.category)}
            />
          ))}
        </Vertical>
      </Vertical>
    </Card>
  )
}

interface IssueCountCardsProps {
  counts: IssueCounts
  selection: IssueSelection
  onSelect: (selection: IssueSelection) => void
}

/**
 * The five count cards, which double as the section's filter: the card picks
 * the severity, a category row inside it narrows further.
 */
export function IssueCountCards({
  counts,
  selection,
  onSelect,
}: IssueCountCardsProps) {
  return (
    <Horizontal gap={8} alignItems='stretch'>
      {ISSUE_BUCKET_KEYS.map((bucket) => (
        <IssueCountCard
          key={bucket}
          bucket={bucket}
          counts={counts[bucket]}
          selection={selection}
          onSelect={onSelect}
        />
      ))}
    </Horizontal>
  )
}
